import { IDataEventsClient } from "@multiweb/clients/dataEvents";
import { ISectionLogger } from "@multiweb/logging";
import { IDataEvent } from "@multiweb/types/contracts";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { DataEventListener, IDataEventsManager, SysteCacheType } from "./IDataEventsManager";
import { InstanceManager } from "./InstanceManager";

export class DataEventsManager implements IDataEventsManager {
    private readonly _logger: ISectionLogger;
    private readonly _client: IDataEventsClient;
    private readonly _subscribes: CaseInsensitiveMap<Array<(dataEvent: IDataEvent) => void>> = new CaseInsensitiveMap<Array<(dataEvent: IDataEvent) => void>>();

    constructor(client: IDataEventsClient, logger: ISectionLogger) {
        this._client = client;
        this._logger = logger;
    }

    private _getSubscribeId(source: string, type: string, subType: string): string {
        return `${source}|${type}|${subType}`; // `
    }

    private _registerSubscribe(source: string, type: string, subType: string, listener: DataEventListener): void {
        const id = this._getSubscribeId(source, type, subType);
        if (!this._subscribes.has(id))
            this._subscribes.set(id, []);

        this._subscribes.get(id).push(listener);
    }

    /**
     * Odregistruje udalost a vrati true, pokud to byla posledni udalost tohoto subTypu
     */
    private _unregisterSubscribe(source: string, type: string, subType: string, listener: DataEventListener): boolean {
        const id = this._getSubscribeId(source, type, subType);
        if (!this._subscribes.has(id))
            return true;

        const innerList = this._subscribes.get(id);

        if (innerList.includes(listener))
            innerList.splice(innerList.indexOf(listener), 1);

        if (innerList.length == 0) {
            this._subscribes.delete(id);
            return true;
        }

        return false;
    }

    public subscribeDataEvent(source: string, type: string, subType: string, listener: DataEventListener): void {
        this._registerSubscribe(source, type, subType, listener);
        this._client.subscribeDataEvent(source, type, subType, listener);
    }

    public unsubscribeDataEvent(source: string, type: string, subType: string, listener?: (dataEvent: IDataEvent) => void): void {
        if (this._unregisterSubscribe(source, type, subType, listener) || listener)
            this._client.unsubscribeDataEvent(source, type, subType, listener);
    }

    public subscribeObjectsChanges(className: string, listener: DataEventListener): void {
        this.subscribeDataEvent("OW", "ObjectsChanged", className, listener);
    }

    public unsubscribeObjectsChanges(className: string, listener: DataEventListener): void {
        this.unsubscribeDataEvent("OW", "ObjectsChanged", className, listener);
    }

    public subscribeClassChanges(className: string, listener: DataEventListener): void {
        this.subscribeDataEvent("OW", "ClassChanged", className, listener);
    }

    public unsubscribeClassChanges(className: string, listener: DataEventListener): void {
        this.unsubscribeDataEvent("OW", "ClassChanged", className, listener);
    }

    public subscribeWFOpenForm(listener: DataEventListener): void {
        this.subscribeDataEvent("OW", "WFOpenForm", "*", listener);
    }

    public unsubscribeWFOpenForm(listener: DataEventListener): void {
        this.unsubscribeDataEvent("OW", "WFOpenForm", "*", listener);
    }

    public subscribeSystemCacheReloaded(settingsType: SysteCacheType, listener: DataEventListener): void {
        this.subscribeDataEvent("OW", "CacheReloaded", settingsType, listener);
    }

    public unsubscribeSystemCacheReloaded(settingsType: SysteCacheType, listener: DataEventListener): void {
        this.unsubscribeDataEvent("OW", "CacheReloaded", settingsType, listener);
    }

    public subscribeActiveRolesChanged(listener: DataEventListener): void {
        this.subscribeDataEvent("OW", "ActiveRolesChanged", "*", listener);
    }

    public unsubscribeActiveRolesChanged(listener: DataEventListener): void {
        this.unsubscribeDataEvent("OW", "ActiveRolesChanged", "*", listener);
    }

    public watchObjectsChanges(): void {
        this.subscribeObjectsChanges("*", this._onObjectsChanged.bind(this));
        this.subscribeClassChanges("*", this._onClassChanged.bind(this));
    }

    private _onObjectsChanged(data: IDataEvent): void {
        try {
            const instanceIds = data.message.body as string[];
            for (const i of instanceIds) {
                const obj = InstanceManager.getInstance(i);
                if (obj && !obj.isDisposed) {
                    obj.refresh();
                    this._logger.info(`Refreshing object '${obj.instanceID}' id '${obj.id}' of class '${obj.objectName}' (with data from application service).`);
                }
            }
        } catch (e) {
            this._logger.warn(e);
        }
    }

    private _onClassChanged(data: IDataEvent): void {
        try {
            const message = data.message;
            if (message.id == null || message.id === "")
                return;

            const objs = InstanceManager.getObjects(message.subType, message.id);
            if (objs != null)
                for (const obj of objs)
                    if (!obj.isChanged && !obj.isDisposed) {
                        obj.reload();
                        this._logger.info(`Reloading object '${obj.instanceID}' id '${obj.id}' of class '${obj.objectName}' (with data from persistent storage).`);
                    }
        } catch (e) {
            this._logger.warn(e);
        }
    }
}
